/**
 * src/components/ReferralRedeemer.tsx
 *
 * Captures a `?ref=<code>` query param on /signup, stashes it in
 * sessionStorage so it survives the Clerk → AuthClerk redirect, and
 * calls /RedeemReferralCode once the customer is authenticated. Clears
 * the storage on completion (success OR known soft-failure) so we don't
 * retry on every render.
 *
 * Self-gates on auth + presence of a pending code; renders nothing.
 *
 * Mounted once in AppShell next to ClerkBridge.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { redeemReferralCode } from '../api/referralApi';

const PENDING_REF_KEY = 'attic_pending_referral_code';

function readPending(): string | null {
  try { return sessionStorage.getItem(PENDING_REF_KEY); } catch { return null; }
}
function writePending(code: string): void {
  try { sessionStorage.setItem(PENDING_REF_KEY, code); } catch { /* noop */ }
}
function clearPending(): void {
  try { sessionStorage.removeItem(PENDING_REF_KEY); } catch { /* noop */ }
}

export function ReferralRedeemer() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const attemptedRef = useRef(false);

  // Capture ?ref=<code> from the URL and stash for after auth completes.
  // Runs on every location change so deeplinks like /signup?ref=K7M4QR or
  // /?ref=K7M4QR all work — Clerk strips query params after handshake.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('ref');
    if (typeof code === 'string' && code.trim()) {
      writePending(code.trim().toUpperCase());
    }
  }, [location.search]);

  // Once authenticated, fire-and-forget the redeem call. Server validates
  // (rejects self-refer, no double-redeem, etc.) and is idempotent.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (attemptedRef.current) return;
    const pending = readPending();
    if (!pending) return;
    attemptedRef.current = true;
    void (async () => {
      try {
        await redeemReferralCode(pending);
      } catch (err) {
        // Server-side validation issues come back as ok:false (not throws),
        // so a thrown error here means a real network/auth problem. Log
        // and clear so we don't loop on it.
        // eslint-disable-next-line no-console
        console.warn('[ReferralRedeemer] redeem call failed', err);
      } finally {
        clearPending();
      }
    })();
  }, [isAuthenticated]);

  return null;
}
